const VERDICT_LABELS = {
  PASS: "Conforme",
  REVIEW: "À vérifier",
  FAIL: "Non conforme",
};

const FIELD_LABELS = {
  sellerName: "Vendeur",
  buyerName: "Acheteur",
  purchaseDate: "Date d'achat",
  productTitle: "Titre du produit",
  referenceId: "Référence",
  unitPrice: "Prix unitaire",
  holderProofDocumentType: "Justificatif — type de document",
  holderProofSubscriberIdentified: "Justificatif — adhérent identifié",
  holderProofInvoiceHolderIdentified: "Justificatif — titulaire de la facture identifié",
  holderProofLinkEstablished: "Justificatif — lien établi",
};

const DOCUMENT_TYPE_LABELS = {
  invoice: "Facture",
  receipt: "Ticket de caisse",
  other: "Autre",
};

const SCORE_LABELS = {
  sellerNameSimilarity: "Similarité vendeur",
  buyerNameSimilarity: "Similarité acheteur",
  titleSimilarity: "Similarité titre",
  referenceIdDistance: "Distance référence",
  dateDeltaDays: "Écart de date",
};

const SIMILARITY_KEYS = new Set(["sellerNameSimilarity", "buyerNameSimilarity", "titleSimilarity"]);

export function verdictLabel(verdict) {
  return VERDICT_LABELS[verdict] || verdict || "—";
}

export function badgeClassFor(verdict) {
  switch (verdict) {
    case "PASS":
      return "bg-green-100 text-green-800";
    case "REVIEW":
      return "bg-yellow-100 text-yellow-800";
    case "FAIL":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-700";
  }
}

export function mapExtractedFields(analysis) {
  const invoice = analysis?.extractedInvoice;
  if (!invoice) return {};
  return {
    Type: DOCUMENT_TYPE_LABELS[invoice.documentType] || invoice.documentType,
    Vendeur: invoice.sellerName,
    Acheteur: invoice.buyerName,
    "Date d'achat": invoice.purchaseDate,
  };
}

function toCheck(field, data) {
  return {
    field,
    label: FIELD_LABELS[field] || field,
    verdict: data?.verdict,
    reason: data?.reason,
  };
}

// Sous-checks du justificatif titulaire, préfixés pour ne pas entrer en collision avec ceux de la facture.
// Un sous-check null n'a pas été exécuté (ex. titulaire de la facture inconnu) : on l'ignore.
function holderProofChecks(analysis) {
  const rawChecks = analysis?.invoiceHolderProof?.checks;
  if (!rawChecks) return [];
  return Object.entries(rawChecks)
    .filter(([, data]) => data)
    .map(([key, data]) => toCheck(`holderProof${key.charAt(0).toUpperCase()}${key.slice(1)}`, data));
}

// Les sous-checks du justificatif titulaire s'affichent juste sous l'acheteur, qu'ils ont servi à trancher.
export function mapChecks(analysis) {
  const rawChecks = analysis?.checks;
  if (!rawChecks) return [];
  const checks = Object.entries(rawChecks).map(([field, data]) => toCheck(field, data));
  const buyerIndex = checks.findIndex((check) => check.field === "buyerName");
  const insertAt = buyerIndex === -1 ? checks.length : buyerIndex + 1;
  checks.splice(insertAt, 0, ...holderProofChecks(analysis));
  return checks;
}

export function formatScoreValue(key, value) {
  if (SIMILARITY_KEYS.has(key)) {
    return `${Math.round(value * 100)} %`;
  }
  if (key === "dateDeltaDays") {
    const days = Math.abs(Number(value));
    if (days === 0) return "Même jour";
    return days === 1 ? "1 jour d'écart" : `${days} jours d'écart`;
  }
  if (key === "referenceIdDistance") {
    const distance = Number(value);
    if (distance === 0) return "Correspondance exacte";
    return distance === 1 ? "1 caractère de différence" : `${distance} caractères de différence`;
  }
  return value;
}

export function mapFormattedScores(analysis) {
  const rawScores = analysis?.scores;
  if (!rawScores) return [];
  return Object.entries(rawScores)
    .filter(([, value]) => value !== null && value !== undefined)
    .map(([key, value]) => ({
      key,
      label: SCORE_LABELS[key] || key,
      value: formatScoreValue(key, value),
    }));
}

export function formatAnalyzedAt(analyzedAt) {
  if (!analyzedAt) return "";
  return new Date(analyzedAt).toLocaleString("fr-FR");
}
